
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import FilterBarContentTypeItem from '@/components/filter-bar/FilterBarContentTypeItem.vue';
import ChipButton from '@/ui-components/ChipButton.vue';
import ChipButtons from '@/ui-components/ChipButtons.vue';

import { calculateUrl } from '@/helpers/state-helper';

import { CollectionType } from '@/enums/collection-type';

import { FilterCollection, FilterName } from '@/stores/filter.store';

import { NewPageType } from '@/@types/graphql-types';
import { providerPageExpTrackEvent } from '@/helpers/experiments/popular-provider-page-exp-helper';
import { isAnyFilterActive, isFilterActive } from '@/helpers/filter-helper';
import { ContentType } from '@/interfaces/filter-bar';
import Genre from '@/interfaces/genre';
import Provider from '@/interfaces/provider';
import { PopularTabType, TitleObjectType } from '@/interfaces/titles';
import { RoutingState } from '@/stores/routing.store';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Routing = namespace('routing');
@Component({
	name: 'FilterBarContentType',
	components: { FilterBarContentTypeItem, ChipButtons, ChipButton },
})
export default class FilterBarContentType extends Vue {
	@Prop() collectionType: CollectionType;
	@Prop() currentFilters: FilterCollection;
	@Prop({ default: false }) showDealsSubscriptionTab: boolean;

	@Constant.Getter providersByShortName: Record<string, Provider>;
	@Constant.Getter genresByShortName: Record<string, Genre>;

	@Routing.State lastRoute: RoutingState['lastRoute'];

	@Filter.Action toggle: (payload: { filterName: FilterName; value: any; selectedState: 1 | 0 }) => void;

	TitleObjectType = TitleObjectType;

	get contentTypes(): ContentType[] {
		return [
			{
				key: 'ALL_TITLES',
				translation: this.$tc('WEBAPP_ALL_TITLES'),
				value: null,
				class: 'filter-bar-content-type__button--all',
				active: !isAnyFilterActive(['content_types'], this.currentFilters),
				hide: false,
				tabName: PopularTabType.ALL,
			},
			{
				key: 'MOVIES',
				translation: this.$tc('WEBAPP_MOVIES'),
				value: TitleObjectType.MOVIE,
				class: 'filter-bar-content-type__button--movies',
				active: isFilterActive('content_types', TitleObjectType.MOVIE, this.currentFilters),
				hide: false,
				tabName: PopularTabType.MOVIE,
			},
			{
				key: 'TVSHOWS',
				translation: this.$tc('WEBAPP_TVSHOWS'),
				value: TitleObjectType.SHOW,
				class: 'filter-bar-content-type__button--tv-shows',
				active: isFilterActive('content_types', TitleObjectType.SHOW, this.currentFilters),
				hide: false,
				tabName: PopularTabType.SHOW,
			},
			// START POPULAR-PROVIDER-PAGE-EXP
			{
				key: 'DEALS_SUBSCRIPTIONS',
				translation: 'Plans',
				value: TitleObjectType.DEALS_SUBSCRIPTION,
				class: 'filter-bar-content-type__button--deals-subscription',
				active: isFilterActive('content_types', TitleObjectType.DEALS_SUBSCRIPTION, this.currentFilters),
				hide: !this.showDealsSubscriptionTab,
				tabName: PopularTabType.DEALS_SUBSCRIPTION,
			},
			// END POPULAR-PROVIDER-PAGE-EXP
		].filter(el => !el.hide);
	}

	get contentTypeUrls() {
		const providersByShortName = this.providersByShortName;
		const genresByShortName = this.genresByShortName;
		const newPageType = this.$route.meta?.newPageType as NewPageType;
		const newPageTypeToCollectionTypes = {
			[NewPageType.New]: CollectionType.NEW,
			[NewPageType.PriceDrop]: CollectionType.PRICEDROPS,
		} as Partial<Record<NewPageType, CollectionType>>;
		return this.contentTypes.reduce((root, item) => {
			root[item.key] = calculateUrl(
				this.$router,
				undefined,
				newPageTypeToCollectionTypes[newPageType] ?? this.collectionType,
				{
					...this.currentFilters,
					content_types: item.value !== null ? [item.value!] : [], // why null[]?
				},
				providersByShortName,
				genresByShortName
			);
			return root;
		}, {} as Record<string, string>);
	}

	contentTypeClicked(contentType: {
		active: ContentType['active'];
		value: ContentType['value'];
		tabName?: PopularTabType;
	}) {
		const selectedState: 1 | 0 = contentType.active ? 0 : 1;
		this.toggle({
			filterName: 'content_types',
			value: contentType.value,
			selectedState,
		});
		providerPageExpTrackEvent(
			{
				label: 'filter_toggled_content_types',
				action: 'click',
			},
			this.lastRoute?.name as string
		);
	}
}
